import { useState, useEffect, useRef, useCallback } from 'react';
import { checkServerStatus, getQueueStatus } from '../services/itoraApi';

export const useWebSocket = (onMessage, onStatusChange) => {
  const [isConnected, setIsConnected] = useState(false);
  const [logs, setLogs] = useState([]);
  const [currentPromptId, setCurrentPromptId] = useState(null);
  const [hasProgress, setHasProgress] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [queueInfo, setQueueInfo] = useState({ queue_remaining: 0, queue_position: 0 });
  const pollRef = useRef(null);
  const runningPromptRef = useRef(null);
  const connStatusRef = useRef(connectionStatus);
  const onMessageRef = useRef(onMessage);
  const onStatusChangeRef = useRef(onStatusChange);
  onMessageRef.current = onMessage;
  onStatusChangeRef.current = onStatusChange;

  useEffect(() => {
    connStatusRef.current = connectionStatus;
  }, [connectionStatus]);

  const addLog = useCallback((message) => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, { timestamp, message }]);
  }, []);

  const updateStatus = useCallback((status) => {
    setConnectionStatus(status);
    if (onStatusChangeRef.current) onStatusChangeRef.current(status);
  }, []);

  const poll = useCallback(async () => {
    try {
      const status = await checkServerStatus();
      if (status.status === 'connected') {
        if (connStatusRef.current !== 'comfyui_connected') {
          updateStatus('comfyui_connected');
          setIsConnected(true);
          addLog('Connected to ComfyUI server');
        }
      } else {
        updateStatus('disconnected');
        setIsConnected(false);
      }
    } catch {
      updateStatus('disconnected');
      setIsConnected(false);
    }

    try {
      const queueData = await getQueueStatus();
      if (queueData && queueData.queue_running !== undefined) {
        const running = queueData.queue_running || [];
        const pending = queueData.queue_pending || [];
        const queueRemaining = running.length + pending.length;

        setQueueInfo(prev => ({ ...prev, queue_remaining: queueRemaining }));

        const msg = {
          type: 'status',
          data: {
            status: {
              exec_info: { queue_remaining: queueRemaining }
            }
          }
        };
        if (onMessageRef.current) onMessageRef.current(msg);

        if (running.length > 0) {
          const first = running[0];
          const promptId = first[1];
          if (promptId !== runningPromptRef.current) {
            runningPromptRef.current = promptId;
            setCurrentPromptId(promptId);
            setHasProgress(true);
            if (onMessageRef.current) {
              onMessageRef.current({ type: 'execution_start', data: { prompt_id: promptId } });
              onMessageRef.current({ type: 'executing', data: { node: '…', prompt_id: promptId } });
            }
            addLog(`Execution started for prompt: ${promptId}`);
          }

          const stepInfo = first[3]?.['0']?.step_info?.current;
          const totalSteps = first[3]?.['0']?.step_info?.total;
          if (stepInfo !== undefined && totalSteps !== undefined) {
            if (onMessageRef.current) {
              onMessageRef.current({
                type: 'progress',
                data: { value: stepInfo, max: totalSteps, prompt_id: promptId }
              });
            }
          }
        } else if (pending.length === 0 && runningPromptRef.current) {
          const completedId = runningPromptRef.current;
          runningPromptRef.current = null;
          setHasProgress(false);
          addLog(`Execution completed for prompt: ${completedId}`);
        }
      }
    } catch {
    }
  }, [addLog, updateStatus]);

  const startPolling = useCallback(() => {
    if (pollRef.current) return;
    updateStatus('connecting');
    addLog('Starting HTTP polling…');
    poll();
    pollRef.current = setInterval(poll, 3000);
  }, [poll, addLog, updateStatus]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    setIsConnected(false);
    updateStatus('disconnected');
    setHasProgress(false);
  }, [updateStatus]);

  const sendMessage = useCallback(() => true, []);

  const requestQueueInfo = useCallback(() => {
    poll();
  }, [poll]);

  useEffect(() => {
    startPolling();
    return () => stopPolling();
  }, [startPolling, stopPolling]);

  return {
    isConnected,
    logs,
    currentPromptId,
    hasProgress,
    connectionStatus,
    queueInfo,
    sendMessage,
    requestQueueInfo,
    disconnect: stopPolling,
    reconnect: startPolling
  };
};
